import { Point, PointTopLeft } from "openfin/_v2/api/system/point";
import { ElementRef, NgZone } from "@angular/core";
import { Dialog } from "@bfx/primeng/dialog";

import { GLPopoutConfiguration } from "../golden-layout/golden-layout-service";
import { Subject, ReplaySubject } from "rxjs";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Router } from "@angular/router";

import { NativeDesktopSupportShared } from "../../shared/native-desktop/NativeDesktopSupportShared";
import { ResizeObserverScheduler } from "../../shared/helper-services/resize-observer-scheduler";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import {
  MAIN_FRAME_ID,
  MonitorInfo,
  Rect,
  WindowOptions,
} from "shared/native-desktop/NativeDesktopData";
import {
  NativeDesktopWindowOptions,
  WindowType,
} from "shared/native-desktop/NativeDesktopWindowOptions";
import { NativeDesktopWindowRegistry } from "./native-desktop-window-registry";
import { NativeDesktopDialog } from "./native-desktop-dialog";

export const ZOOM_VALUE_SMALL: any = {
  label: "Small",
  value: "0",
  zoom: -0.6,
  actualSize: 0.9,
};
export const ZOOM_VALUE_STANDARD: any = {
  label: "Standard",
  value: "1",
  zoom: 0,
  actualSize: 1.0,
};
export const ZOOM_VALUE_LARGE: any = {
  label: "Large",
  value: "2",
  zoom: 0.5,
  actualSize: 1.1,
};
export const ZOOM_VALUE_VERY_LARGE: any = {
  label: "Very Large",
  value: "3",
  zoom: 1.2,
  actualSize: 1.25,
};

export const ZOOM_VALUES: any[] = [
  ZOOM_VALUE_SMALL,
  ZOOM_VALUE_STANDARD,
  ZOOM_VALUE_LARGE,
  ZOOM_VALUE_VERY_LARGE,
];
export const PREFIX_CMD_ARG: string = "--bcdfx-";

export const ELEMENT_WINDOW_TYPE: string = "windowType";
export const ELEMENT_WINDOW: string = "window";
export const ELEMENT_FOCUS: string = "focussed";

export const ANIMATION_DURATION: number = 0;

export const CLASS_DIALOG_BG: string = "dialog-bg-blur";

export const UUID_ADMIN_APP: string = "OMS_UI_Admin";

export const NATIVE_DESKTOP_NO_DROP_SHADOW = "native-no-drop-shadow";

/**Represents a a rect within a quadrant with a point to check and the top left corner*/
type Quad = { originX: number; originY: number; checkX: number; checkY: number };
/** Object containing 4 quads  for checking  */
type Quadrants = { tl: Quad; tr: Quad; bl: Quad; br: Quad };

type ActiveRect = {
  screenRects: Rect[];
  availableRects: Rect[];
};

export const ATTR_HREF: string = "href";
export const ID_THEME: string = "theme-id";

export class NativeDesktopSupport extends NativeDesktopSupportShared {
  public static zone: NgZone;

  public static resizeObserver: ResizeObserverScheduler;

  private static _newDocumentListener: (wnd: Document) => any;

  private static _windowRegistry: NativeDesktopWindowRegistry;

  public static initializationSubject: Subject<void> = new ReplaySubject<void>(1);

  public static zoomLevelSubject: Subject<number> = new Subject<number>();

  static resolveNativeWindowState: (value: void | PromiseLike<any>) => void;
  static resolveMainWindowFadeIn: (value: void | PromiseLike<void>) => void;
  static mainWindowLoadComplete: boolean = false;

  // loaded once from profile
  private static nativeWindowState: Promise<any> = new Promise<any>((res) => {
    NativeDesktopSupport.resolveNativeWindowState = res;
  });

  public static nativeWindowFadeIn: Promise<void> = new Promise<void>((res) => {
    NativeDesktopSupport.resolveMainWindowFadeIn = res;
  });

  public static defaultZoomLevel: number = 0;

  public static zoomLevel: number = 1.0;

  public static scaleRatio: number = 1.0;

  private static _zoom: number = 0;

  public static get zoom(): number {
    return NativeDesktopSupport._zoom;
  }

  public static set zoom(value: number) {
    if (value == null) {
      logger.out.error("invalid Zoom set, ignoring");
      NativeDesktopSupport._zoom = 0;
      return;
    }

    NativeDesktopSupport._zoom = value;
  }

  public static diagnosticMode: boolean = false;

  private static applicationElement: any = null;

  private static resizeObserverElementWeakMap = new WeakMap();

  private static idleListenerSet = new Set();

  public static cmdLineArguments: any = {};

  public static initialScreenWidth: any;
  public static initialScreenHeight: number;

  public static minLeft: number = 0;
  public static minTop: number = 0;

  private static readyResolve;
  private static readyReject;

  private static monitorInfo: MonitorInfo = null;

  public static enableDropdowns: boolean = false;

  public static readonly ready = new Promise<void>((res, rej) => {
    NativeDesktopSupport.readyResolve = res;
    NativeDesktopSupport.readyReject = rej;
  });

  public static initNativeDesktop(): boolean {
    const result = NativeDesktopSupport.isNativeDesktopContainer();

    try {
      if (!result) {
        logger.out.info("Running browser app...");
        NativeDesktopSupport.setReady();
        return false;
      }

      logger.out.info("Running Thick client ...");

      window.document.body.setAttribute("frameId", MAIN_FRAME_ID);

      NativeDesktopSupport._windowRegistry = new NativeDesktopWindowRegistry();
      NativeDesktopSupportShared._mainWin = new NativeDesktopWindow(window);

      NativeDesktopSupport.desktop.getCommandLineArguments().then((args: string[]) => {
        logger.out.info("Native desktop running using command line arguments: " + args);
        NativeDesktopSupport.devMode = args.includes("--dev");
        NativeDesktopSupport.enableDropdowns = args.includes("--enable-dropdown-wnd");

        NativeDesktopSupport.processCmdArgs(args);

        if (
          (NativeDesktopSupport.diagnosticMode = NativeDesktopSupport.cmdLineArguments.diag >= 0)
        ) {
          logger.out.info("Running in diagnostic mode. Periodic health info dump enabled.");
          setInterval(NativeDesktopSupport.desktop.api.dumpInfo, 10000);
        }
      });

      // Reset to standard level
      NativeDesktopSupport.desktop.api.setZoomFactor(1.0);

      NativeDesktopSupport.desktop.api.getMonitorInfo().then(
        (monitorInfo: MonitorInfo) => {
          NativeDesktopSupport.monitorInfo = monitorInfo;

          NativeDesktopSupport.desktop.on("display-changed", (monitorInfo) => {
            logger.out.info(`Native desktop: Monitor info update ${JSON.stringify(monitorInfo)}`);
            NativeDesktopSupport.monitorInfo = monitorInfo;
          });

          let screenBounds: Rect = monitorInfo.primaryMonitor.monitorRect;
          let screenWidth: number = screenBounds.right;
          let screenHeight: number = screenBounds.bottom;
          NativeDesktopSupport.initialScreenWidth = screenWidth;
          NativeDesktopSupport.initialScreenHeight = screenHeight;

          NativeDesktopSupport.minLeft = monitorInfo.virtualScreen.left;
          NativeDesktopSupport.minTop = monitorInfo.virtualScreen.top;

          logger.out.info("User MonitorInfo = " + JSON.stringify(monitorInfo));
        },
        (err?) => {
          logger.out.error(`NativeDesktopSupport.onLoginResize failed getMonitorInfo() ${err}`);
        },
      );

      // NativeDesktopSupport.wrapDomHandler();
    } catch (err) {
      logger.out.error(`${NativeDesktopSupport.name}.initNativeDesktop() error: `, err);
    }

    return result;
  }

  private static processCmdArgs(args: string[]): void {
    try {
      if (args.length > 0) {
        args
          .filter((arg: string) => arg.startsWith(PREFIX_CMD_ARG))
          .forEach((arg: string) => {
            arg = arg.substring(PREFIX_CMD_ARG.length).trim();
            let argKey: string = arg;
            let argValue: string = "";

            if (arg.indexOf("=") > 0) {
              argKey = arg.substring(0, arg.indexOf("="));
              argValue = arg.substring(arg.indexOf("=") + 1);
            }

            logger.out.info("Native desktop argument: " + argKey + " with value " + argValue);

            NativeDesktopSupport.cmdLineArguments[argKey] = argValue;
          });
      }
    } catch (e) {
      logger.out.error(`Failed to parse command line arguments: ${args}, error: ${e}`);
    }
  }

  public static start(element: Element): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      NativeDesktopSupport.initWithDesktop(element);
    }
  }

  public static initialize(zone: NgZone): void {
    NativeDesktopSupport.zone = zone;

    NativeDesktopSupport.resizeObserver = new ResizeObserverScheduler(
      (entries) => NativeDesktopSupport.onResize(entries),
      NativeDesktopSupport.name,
    );
  }

  private static onResize(entries): Promise<void> {
    return new Promise<void>((all) => {
      const resizePromises = [];

      for (const entry of entries) {
        let target: Element = entry.target;
        const { width, height } = entry.contentRect;

        const noDropShadow = target.classList.contains(NATIVE_DESKTOP_NO_DROP_SHADOW);
        const offsetHeight = target["offsetHeight"] + (noDropShadow ? 0 : 10);
        const offsetWidth = target["offsetWidth"] + (noDropShadow ? 0 : 15);

        if (target[ELEMENT_WINDOW] && width > 0 && height > 0) {
          let w: number = offsetWidth
            ? offsetWidth * NativeDesktopSupport.zoomLevel
            : (width + 13) * NativeDesktopSupport.zoomLevel;
          let h: number = offsetHeight
            ? offsetHeight * NativeDesktopSupport.zoomLevel
            : (height + 5) * NativeDesktopSupport.zoomLevel;
          resizePromises.push(
            new Promise<void>((res) => {
              (target[ELEMENT_WINDOW] as NativeDesktopWindow)
                .setSize(w, h)
                .then(() => res())
                .catch((err) => {
                  logger.out.error(`${this.constructor.name} resizeTo() failed ${err}`);
                  res();
                });
            }),
          );
        }
      }

      Promise.all(resizePromises).then(() => all());
    });
  }

  public static closeAllWindows(): void {
    // fin.desktop.Application.getCurrent().getChildWindows((children: fin.OpenFinWindow[]) => {
    //   children.forEach((wnd: fin.OpenFinWindow) => wnd.close());
    // });
  }

  public static initWithDesktop(element: Element): void {
    NativeDesktopSupport.initializationSubject.next();
    element.ownerDocument.body["style"]["-webkit-app-region"] = "drag";
  }

  public static initWindowPools(): void {
    if (!NativeDesktopSupport.isNativeDesktopContainer()) {
      return;
    }
  }

  public static getZoomLevel(): number {
    return NativeDesktopSupport.zoomLevel;
  }

  public static setZoomLevel(level: number, skipWindowScale: boolean = false): Promise<void> {
    let oldZoom: number = NativeDesktopSupport.zoomLevel;

    let currentValue: number = ZOOM_VALUES.filter((zoom: any) => zoom.zoom == level)[0].actualSize;
    NativeDesktopSupport.zoomLevel = currentValue;
    NativeDesktopSupport.scaleRatio = currentValue;
    NativeDesktopSupport.zoom = level;

    if (currentValue != oldZoom) {
      NativeDesktopSupport.zoomLevelSubject.next(currentValue);
    }

    return this.desktop.api.setZoomFactor(currentValue);
  }

  public static initNewDocumentListener(newDocumentListener: (document: Document) => any): void {
    NativeDesktopSupport._newDocumentListener = newDocumentListener;
  }

  public static getNewDocumentListener(): (document: Document) => any {
    return NativeDesktopSupport._newDocumentListener;
  }

  public static onApplicationInitialized(element: Element): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.restoreNativeWindowState(element);
    }
  }

  public static openWindow(
    title: string,
    windowType: WindowType,
    allowMultiple: boolean,
    config: GLPopoutConfiguration,
    callback: (window: NativeDesktopWindow) => any,
    url: string = "",
  ): void {
    let options: any = NativeDesktopWindowOptions.withIdAndType(title, windowType);

    if (config && config.minimumSize) {
      options.minWidth = config.minimumSize.width ?? null;
      options.minHeight = config.minimumSize.height ?? null;
    }

    if (config && config.preferredSize) {
      options.width = config.preferredSize.width ?? null;
      options.height = config.preferredSize.height ?? null;
    }

    if (config && config.maximumSize) {
      options.maxWidth = config.maximumSize.width ?? null;
      options.maxHeight = config.maximumSize.height ?? null;
    }

    let wndId = allowMultiple ? `${title}-${Math.random()}` : title;

    let wnd = window.open(url, wndId, options.toString());

    if (url.length > 0) {
      wnd.addEventListener("load", () => {
        this.setupWindow(wndId, title, wnd, false, callback);
      });
    } else {
      this.setupWindow(wndId, title, wnd, true, callback);
    }
  }

  private static setupWindow(
    wndId: string,
    title: string,
    wnd: Window,
    prepare: boolean = true,
    callback: (window: NativeDesktopWindow) => any,
  ): void {
    wnd.document.body.setAttribute("frameId", wndId);

    let nativeWindow = NativeDesktopSupport._windowRegistry.get(wndId);

    if (prepare) {
      logger.out.info(`NativeDesktop: Preparing window ${wndId} ...`);
      this.prepareWindow(title, wnd).then(() => {
        callback(nativeWindow);
      });
    } else {
      logger.out.info(`NativeDesktop: Launching window ${wndId} with no prepare`);
      callback(nativeWindow);
    }
  }

  private static prepareWindow(title: string, wnd: Window): Promise<void> {
    return new Promise<void>((resolve) => {
      wnd.document.body["style"].overflow = "hidden";
      wnd.document.body.classList.add("desktop");

      let nodes = Array.from(window.document.head.children)
        .filter((e) => e.tagName.toLowerCase() === "link")
        .filter((e) => e.getAttribute("rel") === "stylesheet")
        .map((e) => e.cloneNode());

      let counter: number = nodes.length;
      nodes.forEach((n) => {
        n.addEventListener("load", () => {
          counter--;

          if (counter <= 0) {
            resolve();
          }
        });
      });

      let titleElement = wnd.document.createElement("title");
      titleElement.innerText = title;

      wnd.document.head.append(titleElement);
      wnd.document.head.append(...nodes);
    });
  }

  private static restoreNativeWindowState(element: Element): void {
    NativeDesktopSupport.applicationElement = element;

    element.ownerDocument.body["style"]["-webkit-app-region"] = null;

    NativeDesktopSupport.nativeWindowState.then((winConfig) => {
      logger.out.info(
        "restoreNativeWindowState: winConfig = " + winConfig ? JSON.stringify(winConfig) : "None",
      );

      const outOfBounds = NativeDesktopSupport.isWindowConfigOutOfBounds(winConfig);
      if (outOfBounds) {
        logger.out.warn("Not restoring window - marked outOfBounds:" + JSON.stringify(winConfig));
        return;
      }
      if (
        winConfig == null ||
        typeof winConfig.left !== "number" ||
        typeof winConfig.top !== "number" ||
        typeof winConfig.width !== "number" ||
        typeof winConfig.height !== "number" ||
        outOfBounds
      ) {
        return NativeDesktopSupport.createMainWindowState();
      } else {
        return NativeDesktopSupport.restoreMainWindowState(winConfig);
      }
    });
  }

  /**
   * Checks to ensure we don't resore a window with a stored config such as:
   * :{left:-32640,top:-32000,width:237,height:39,state:"minimized"}
   * @param winConfig
   * @returns
   */
  public static isWindowConfigOutOfBounds(winConfig): boolean {
    return (
      winConfig &&
      winConfig.state === "minimized" &&
      (winConfig.left + winConfig.width < NativeDesktopSupport.minLeft ||
        winConfig.top + winConfig.height < NativeDesktopSupport.minTop)
    );
  }

  public static restoreMainWindowState(winConfig): void {
    let element = NativeDesktopSupport.applicationElement;

    const errCallback = (err) => {
      logger.out.info(`${this.constructor.name}.restoreMainWindowState() failed`, err);
      NativeDesktopSupport.mainWindowFadeIn(element);
    };

    NativeDesktopSupport.desktop.api
      .getMonitorInfo()
      .then((monitorInfo: MonitorInfo) => {
        logger.out.info(
          "restoreMainWindowState: winConfig = " + winConfig ? JSON.stringify(winConfig) : "None",
        );
        let visibleRect: Rect = monitorInfo.virtualScreen;

        winConfig.left = Math.max(winConfig.left, visibleRect.left);
        winConfig.top = Math.max(winConfig.top, visibleRect.top);
        winConfig.left = Math.min(winConfig.left, visibleRect.right - winConfig.width);
        winConfig.top = Math.min(winConfig.top, visibleRect.bottom - winConfig.height);

        if (winConfig.top < visibleRect.top) {
          winConfig.top = visibleRect.top;
        }

        if (winConfig.left < visibleRect.left) {
          winConfig.left = visibleRect.left;
        }

        if (winConfig.width > visibleRect.right - visibleRect.left) {
          winConfig.width = visibleRect.right - visibleRect.left;
        }

        if (winConfig.height > visibleRect.bottom - visibleRect.top) {
          winConfig.height = visibleRect.bottom - visibleRect.top;
        }

        NativeDesktopSupport.getNativeDesktopWindow()
          .setBounds(winConfig.left, winConfig.top, winConfig.width, winConfig.height)
          .then(() => {
            NativeDesktopSupport.getNativeDesktopWindow()
              .updateOptions({
                minWidth: 800,
                minHeight: 300,
                resizable: true,
                maximizable: true,
              })
              .then(() => {
                if (winConfig.state == "maximized") {
                  NativeDesktopSupport.getNativeDesktopWindow()
                    .maximize()
                    .then(() => {
                      NativeDesktopSupport.mainWindowFadeIn(element);
                    })
                    .catch(errCallback);
                } else {
                  NativeDesktopSupport.mainWindowFadeIn(element);
                }
              })
              .catch(errCallback);
          })
          .catch(errCallback);
      })
      .catch(errCallback);
  }

  private static createMainWindowState(): void {
    let element = NativeDesktopSupport.applicationElement;

    const errCallback = (err) => {
      logger.out.info(`${this.constructor.name}.createMainWindowState() failed`, err);
      NativeDesktopSupport.mainWindowFadeIn(element);
    };

    NativeDesktopSupport.desktop.api
      .getMonitorInfo()
      .then((monitorInfo: MonitorInfo) => {
        let monitorBounds: Rect = monitorInfo.primaryMonitor.monitorRect;
        let width: number = monitorBounds.right * 0.8;
        let height: number = monitorBounds.bottom * 0.8;
        let left: number = monitorBounds.right * 0.1;
        let top: number = monitorBounds.bottom * 0.1;

        NativeDesktopSupport.getNativeDesktopWindow()
          .setBounds(left, top, width, height)
          .then(() => {
            let options: WindowOptions = {
              minWidth: 800,
              minHeight: 300,
              resizable: true,
              maximizable: true,
            };

            NativeDesktopSupport.getNativeDesktopWindow()
              .updateOptions(options)
              .then(() => {
                NativeDesktopSupport.mainWindowFadeIn(element);
              })
              .catch(errCallback);
          })
          .catch(errCallback);
      })
      .catch(errCallback);
  }

  private static mainWindowFadeIn(element: Element): void {
    element.children[0]["style"].opacity = 1;
    this.resolveMainWindowFadeIn();
    NativeDesktopSupport.mainWindowLoadComplete = true;
  }

  public static getNativeDesktopWindow(): NativeDesktopWindow {
    return NativeDesktopSupportShared._mainWin;
  }

  public static getWindowById(id: string): NativeDesktopWindow {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      return this._windowRegistry.get(id);
    }

    return null;
  }

  public static getWindowByPopoutId(id: string): NativeDesktopWindow {
    return this._windowRegistry.getAll().find((w) => (w as any).popoutId == id);
  }

  public static getWindow(element: Element): NativeDesktopWindow {
    if (element?.ownerDocument.defaultView === window) {
      return NativeDesktopSupport._mainWin;
    }

    return NativeDesktopSupport._windowRegistry.findWindow(element);
  }

  public static getURL(mainWindow: Window, refValue: string): string {
    let base: string = mainWindow.document.baseURI;
    return base + refValue;
  }

  /**
   * Looks up the currently focused window or the top most parent of it
   * based on the specified parameter.
   * @param findTopWindow look for the top most parent, default is false
   * @returns the currently focused window or it's top most parent
   */
  public static findActive(findTopWindow: boolean = false): Promise<NativeDesktopWindow> {
    return new Promise<NativeDesktopWindow>((resolve) => {
      let activeWindowPromise = findTopWindow
        ? this.desktop._window.getTopActiveFrameName()
        : this.desktop._window.getActiveFrameName();

      activeWindowPromise.then((frameName) => {
        resolve(this._windowRegistry.get(frameName));
      });
    });
  }

  /**
   * Find window that was last marked as active
   * @returns the window that was last marked as active
   */
  public static findMarkedAsActive(): NativeDesktopWindow {
    return this._windowRegistry.getAll().find((win) => {
      return win.getNativeWindow().document.body.classList.contains("active-window");
    });
  }

  public static async prepareEmbeddedDialog(
    bodyElement: Element,
    _dialog: Dialog,
    element: ElementRef,
  ): Promise<void> {
    let dialog: NativeDesktopDialog = NativeDesktopDialog.wrap(
      _dialog,
      NativeDesktopSupport.isNativeDesktopContainer(),
    );

    if (dialog.visible) {
      return;
    }

    dialog.visible = true;

    if (!NativeDesktopSupport.isNativeDesktopContainer()) {
      dialog.addBrowserDialog(document.body);
    } else {
      bodyElement = bodyElement ?? (await this.getActiveBody());

      dialog.addNativeDialog(element, bodyElement);
    }

    return Promise.resolve();
  }

  /**
   *
   * @returns Currently focused window (_this will not represent active window in a breakpoint_).
   */
  private static async getActiveBody() {
    const wnd =
      (await NativeDesktopSupport.findActive(true)) ?? NativeDesktopSupport.findMarkedAsActive();
    return wnd?.getNativeWindow?.()?.document?.body;
  }

  public static attachResizeObserver(element: Element, win: NativeDesktopWindow): void {
    if (element instanceof Element === false) {
      logger.out.error(
        `${this.constructor.name}.attachResizeObserver(%O, %O) invalid element`,
        element,
        window,
      );
      return;
    }

    element[ELEMENT_WINDOW] = win;
    NativeDesktopSupport.resizeObserver.observe(element);
    NativeDesktopSupport.resizeObserverElementWeakMap.set(win, element);
  }

  public static detachResizeObserver(component): void {
    let rootNode: Element = component.location.nativeElement as Element;
    if (rootNode != null && rootNode instanceof Element) {
      let rootNodeElement: Element = rootNode.firstElementChild;

      if (rootNodeElement != null && rootNodeElement instanceof Element) {
        NativeDesktopSupport.resizeObserver.unobserve(rootNodeElement);
      }
    }
  }

  public static attachCloseObserver(
    win: NativeDesktopWindow,
    callback: (win: NativeDesktopWindow, event: any) => boolean,
  ): void {
    win.getNativeWindow().addEventListener(
      "beforeunload",
      function (event) {
        if (NativeDesktopSupport.resizeObserverElementWeakMap.has(win)) {
          let element = NativeDesktopSupport.resizeObserverElementWeakMap.get(win);
          if (element != null && element instanceof Element) {
            NativeDesktopSupport.resizeObserver.unobserve(element);
          }
          delete element[ELEMENT_WINDOW];
        }

        if (callback(win, event)) {
          win.close();
        }
      }.bind(win),
    );
  }

  /* @deprecated no-op after WindowPool removal */
  public static closeAllSmallWindows(eventName: string): void {
    // TODO ELECTRON
  }

  public static prepareGridOptions(gridOptions: any, element: Element): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      gridOptions.getDocument = () => {
        return element.ownerDocument;
      };
    }
  }

  public static activateAllWindows(): Promise<void> {
    return this.desktop.api.activateAllWindows();
  }

  /**
   * avoid taking focus while taking menu, tooltip and other utility types to foreground.
   */
  public static restoreUtility(wndRef: NativeDesktopWindow): Promise<void> {
    return wndRef.setAsForeground();
  }

  public static getWindowConfig(wnd: NativeDesktopWindow): Promise<{
    left: number;
    top: number;
    width: number;
    height: number;
    state: "normal" | "minimized" | "maximized";
  }> {
    let win: Window = wnd.getNativeWindow();

    return new Promise<any>((res, rej) => {
      wnd.getState().then((state: string) => {
        res({
          left: win.screenX,
          top: win.screenY,
          width: win.outerWidth,
          height: win.outerHeight,
          state: state,
        });
      });
    });
  }

  public static exit(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        let clearCache: boolean = NativeDesktopSupport.cmdLineArguments.nocachedelete == null;
        this.desktop.exit(clearCache);
      }
    }
  }

  public static prepareLogout(): void {
    // close notifications
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      try {
        // TODO ELECTRON
      } catch (error) {
        logger.out.warn("Failed to prepare logout native desktop container " + error);
      }
    }
  }

  public static logout(router: Router, prepare: boolean): boolean {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (prepare) {
        this.prepareLogout();
      }

      // Restart the application to reach the login screen
      NativeDesktopSupport.desktop.api
        .restart()
        .then(() => {
          logger.out.info("Application restarted.");
        })
        .catch((reason: any) => {
          logger.out.error("Failed to restart application: " + reason);
          if (router != null) {
            router.navigate(["/login"]);
          }
        });

      return true;
    }

    return false;
  }

  public static launchAdminApp(uri: string): void {
    let adminAppWindow = NativeDesktopSupport.getWindowById("admin");

    if (adminAppWindow != null) {
      logger.out.info("NativeDesktopSupport: Admin app already running.");
      adminAppWindow.restoreToFront();
    } else {
      logger.out.info("NativeDesktopSupport: Admin app launching ...");
      NativeDesktopSupport.openWindow(
        "admin",
        WindowType.NormalResizable,
        false,
        {
          preferredSize: { width: 1200, height: 800 },
          minimumSize: { width: 1200, height: 800 },
        } as GLPopoutConfiguration,
        (wnd) => {
          logger.out.error(this.constructor.name + ": Admin app window launched");
          wnd.getNativeWindow()["adminWindow"] = true;
          wnd.getNativeWindow().document.body.setAttribute("frameId", "admin");
          wnd.show();
        },
        uri,
      );
    }
  }

  public static closeAdminApp(): Promise<boolean> {
    return new Promise<boolean>((res) => {
      let adminAppWindow = NativeDesktopSupport.getWindowById("admin");
      adminAppWindow.close();
      logger.out.info("NativeDesktopSupport: Admin app closed");
      res(true);
    });
  }

  private static containsPoint(container: Rect, point: PointTopLeft) {
    return (
      point.left >= container.left &&
      point.top >= container.top &&
      point.top <= container.bottom &&
      point.left <= container.right
    );
  }

  /**
   * Get the inital position for a utility window that can be in any of the 4 quadrants
   * around the x,y location. This will return a potentially new x/y such that the utility window can
   * be viewed on a single monitor
   * @param x
   * @param y
   * @param width
   * @param height
   * @returns
   */
  public static getUtilityWindowLocation(x, y, width, height): Promise<Point> {
    logger.out.warn(
      `Getting Utility window location for (x,y) = (${x}, ${y}) - width/height = ${width}, ${height}`,
    );
    return this.getScreenRectForPoint(x, y).then((screenRect) => {
      let retPoint: Point = null;
      if (screenRect) {
        const quads = this.createContextMenuQuads(x, y, width, height);
        retPoint = this.getPreferredOrigin(screenRect, x, y, width, height, quads);
      } else {
        logger.out.warn(
          `Specified window origin is outside of the bounds of all screens: (${x}, ${y})`,
        );
      }

      if (retPoint == null) {
        logger.out.warn(
          `Specified window origin is outside of the bounds of all screens: (${x}, ${y})`,
        );
        retPoint = { x: 0, y: 0 };
      }

      return retPoint;
    });
  }

  /**
   * Get the available screen rect for the specified point
   * @param x
   * @param y
   * @param width
   * @param height
   * @returns
   */
  public static getScreenRectForPoint(x, y): Promise<Rect> {
    return new Promise((res) => {
      let monitorInfo = NativeDesktopSupport.getMonitorInfo();
      const fullrect = monitorInfo.virtualScreen;
      x = Math.max(x, fullrect.left);
      x = Math.min(x, fullrect.right);
      y = Math.max(y, fullrect.top);
      y = Math.min(y, fullrect.bottom);

      let { screenRects, availableRects } = NativeDesktopSupport.getActiveRect(monitorInfo);

      // First figure out what screen the point is on, then determine preferred location on that screen
      let windowLocation: PointTopLeft = { left: x, top: y };
      let retRect: Rect = null;
      for (let i: number = 0; i < screenRects.length; i++) {
        if (this.containsPoint(screenRects[i], windowLocation)) {
          logger.out.info(
            `getWindowLocation(): Found candidate screen for (${x}, ${y}) - has ` +
              JSON.stringify(screenRects[i]),
          );
          retRect = availableRects[i];
          break;
        }
      }
      if (retRect == null) {
        logger.out.warn(
          `Specified window origin is outside of the bounds of all screens: (${x}, ${y})`,
        );
        if (availableRects.length) {
          retRect = availableRects[0];
        }
      }
      res(retRect);
    });
  }

  public static getActiveRect(monitorInfo: MonitorInfo): ActiveRect {
    let screenRects: Rect[] = monitorInfo.primaryMonitor.displayDeviceActive
      ? [monitorInfo.primaryMonitor.monitorRect]
      : [];
    let availableRects: Rect[] = monitorInfo.primaryMonitor.displayDeviceActive
      ? [monitorInfo.primaryMonitor.availableRect]
      : [];

    monitorInfo.nonPrimaryMonitors
      .filter((monitorDetails) => monitorDetails.displayDeviceActive)
      .forEach((monitorDetails) => {
        screenRects.push(monitorDetails.monitorRect);
        availableRects.push(monitorDetails.availableRect);
      });

    // fallback to main window if all screen are marked unavailable (sleep mode?)
    if (screenRects.length === 0 && availableRects.length === 0) {
      screenRects.push(monitorInfo.primaryMonitor.monitorRect);
      availableRects.push(monitorInfo.primaryMonitor.availableRect);
    }

    return { screenRects, availableRects };
  }

  /**
   * Array of quads to check in preferred order for Context menus
   * @param x
   * @param y
   * @param width
   * @param height
   * @returns
   */
  private static createContextMenuQuads(x, y, width, height): Quad[] {
    // Create 4 quadrants, anchored around (x, y)
    const clickRect = { top: y, bottom: y, left: x, right: x };
    const quadrants = this.createQuads(clickRect, width, height);

    return [quadrants.br, quadrants.bl, quadrants.tr, quadrants.tl];
  }

  /**
   * Create a set of quadrants (bottom-right, bottom-left, top-right, top-left)
   * around the specified click rectangle such that the quadrants are
   * outside of the bounds of the click rectangle but share a top or bottom edge - this is generally how users
   * expect popups to be aligned
   * @param clickRect
   * @param width
   * @param height
   * @returns
   */
  private static createQuads(clickRect: Rect, width, height): Quadrants {
    // Create 4 quadrants, anchored around the clickRect rectangle such that the rect is not obscured
    // bottom-right
    let x = clickRect.left,
      y = clickRect.bottom;
    const br = { originX: x, originY: y, checkX: x + width, checkY: y + height };
    //bottom-left
    (x = clickRect.right), (y = clickRect.bottom);
    const bl = { originX: x - width, originY: y, checkX: x - width, checkY: y + height };
    // top-right
    (x = clickRect.left), (y = clickRect.top);
    const tr = { originX: x, originY: y - height, checkX: x + width, checkY: y - height };
    // top-left
    (x = clickRect.right), (y = clickRect.top);
    const tl = { originX: x - width, originY: y - height, checkX: x - width, checkY: y - height };
    return { tl, tr, bl, br };
  }

  /**
   *
   * Checks all input quads to see if scree contains the (checkX, checkY) point . If found,
   * it will return the origin of the quad for displaying the utility window. otherwise it will attempt to
   * find an onscreen x,y where the bounds of the quad a re visible
   *
   * If non-is found it will return a default.
   * @param screenRect
   * @param x
   * @param y
   * @param width
   * @param height
   * @returns
   */
  private static getPreferredOrigin(
    screenRect: Rect,
    x,
    y,
    width,
    height,
    windowRects: Quad[],
  ): Point {
    logger.out.info(`Now finding orientation for  (width:${width}, height:${height})`);
    let retPoint: Point = null;

    for (let i = 0; i < windowRects.length; i++) {
      if (
        this.containsPoint(screenRect, { left: windowRects[i].checkX, top: windowRects[i].checkY })
      ) {
        retPoint = { x: windowRects[i].originX, y: windowRects[i].originY };
        logger.out.info(`Found root inclusive (x:${retPoint.x}, y:${retPoint.y})`);
        return retPoint;
      }
    }

    // The supplied rect will not fit inside the screen anchored to x and y
    let newX: number, newY: number;
    if (x + width <= screenRect.right) {
      newX = x;
    } else if (x - width >= screenRect.left) {
      newX = x - width;
    } else {
      // now enough room either side so prefer to anchor on right
      newX = Math.max(0, screenRect.right - width);
    }
    if (y + height <= screenRect.bottom) {
      newY = y;
    } else if (y - height >= screenRect.top) {
      newY = y - height;
    } else {
      // now enough room up or down so prefer to anchor on bottom
      newY = Math.max(0, screenRect.bottom - height);
    }

    retPoint = { x: newX, y: newY };

    return retPoint;
  }

  public static getMonitorInfo(): MonitorInfo {
    return NativeDesktopSupport.monitorInfo;
  }

  public static getAllOpenWindows(): NativeDesktopWindow[] {
    return this._windowRegistry.getAll();
  }

  public static isReady(): Promise<void> {
    return NativeDesktopSupport.ready;
  }

  public static setReady(): void {
    NativeDesktopSupport.readyResolve();
  }

  /**
   * Remove the main window frame. Convenience thing
   * for SSO login
   */
  public static removeMainWindowFrame(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let options = { frame: false };
      // TODO ELECTRON
      // NativeDesktopSupport.getNativeDesktopWindow().updateOptions(options);
    }
  }

  public static updateWindowTheme(name) {
    requestAnimationFrame(() => {
      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        logger.out.info("NativeDesktopSupport: Updating window theme ... ", name);

        try {
          let allWindows: NativeDesktopWindow[] = this._windowRegistry.getAll();

          let mainWindow: Window = NativeDesktopSupport.getNativeDesktopWindow().getNativeWindow();
          let href: string = mainWindow.document.getElementById(ID_THEME).getAttribute(ATTR_HREF);
          href = NativeDesktopSupport.getURL(window, href);

          allWindows
            .filter((w) => w != NativeDesktopSupport._mainWin)
            .forEach((w) => {
              w.getNativeWindow().document.getElementById(ID_THEME).setAttribute(ATTR_HREF, href);
            });
        } catch (e) {
          logger.out.error(`NativeDesktopSupport: Error updating theme ${e}`);
        }
      }
    });
  }
}
